import React, { useState } from 'react';
import { 
  Flower2, Building2, Send, CheckCircle, AlertCircle,
  Globe, Heart, Briefcase, Users, ArrowLeft, Upload, Image
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from '../hooks/use-toast';
import api from '../services/api';

const PartnerApply = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [formData, setFormData] = useState({
    organizationName: '',
    organizationType: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    country: '',
    website: '',
    logo: '',
    description: '',
    mission: '',
    targetAudience: '',
    partnershipGoals: '',
    hasMarketplace: false,
    marketplaceUrl: '',
    marketplaceDescription: ''
  });

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Erreur',
        description: 'Veuillez sélectionner une image (PNG, JPG, etc.)',
        variant: 'destructive'
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: 'Erreur',
        description: 'L\'image ne doit pas dépasser 5 Mo',
        variant: 'destructive'
      });
      return;
    }

    setUploadingLogo(true);

    try {
      // Convert to base64 for preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
        // Store base64 data (in production, you would upload to a file server)
        updateField('logo', reader.result);
        setUploadingLogo(false);
        toast({
          title: 'Logo téléchargé',
          description: 'Votre logo a été ajouté avec succès'
        });
      };
      reader.readAsDataURL(file);
    } catch (error) {
      setUploadingLogo(false);
      toast({
        title: 'Erreur',
        description: 'Erreur lors du téléchargement du logo',
        variant: 'destructive'
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post('/partners/apply', formData);
      setSubmitted(true);
      toast({
        title: 'Demande envoyée!',
        description: response.data.message
      });
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Erreur lors de l\'envoi',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const organizationTypes = [
    { value: 'government', label: 'Gouvernement', icon: Building2, description: 'Agences gouvernementales et administrations publiques' },
    { value: 'ngo', label: 'ONG', icon: Heart, description: 'Organisations non gouvernementales et associations' },
    { value: 'international_org', label: 'Organisation Internationale', icon: Globe, description: 'OMS, UNICEF, UNFPA, etc.' },
    { value: 'pharmaceutical', label: 'Laboratoire Pharmaceutique', icon: Briefcase, description: 'Industrie pharmaceutique et biotechnologie' },
    { value: 'ministry', label: 'Ministère de la Santé', icon: Users, description: 'Ministères et directions de la santé publique' },
    { value: 'enterprise', label: 'Entreprise', icon: Briefcase, description: 'Entreprises et sociétés commerciales' }
  ];

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full p-8 bg-white/10 backdrop-blur-md border-white/20 text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center">
            <CheckCircle className="w-10 h-10 text-green-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-4">
            Demande Envoyée!
          </h2>
          <p className="text-pink-200 mb-6">
            Merci pour votre intérêt à devenir partenaire de Fleur de Vie. 
            Notre équipe examinera votre demande et vous contactera sous 5-7 jours ouvrables.
          </p>
          <div className="space-y-3">
            <Link to="/partners">
              <Button className="w-full bg-gradient-to-r from-pink-500 to-purple-600">
                Voir nos partenaires
              </Button>
            </Link>
            <Link to="/">
              <Button variant="outline" className="w-full border-white/30 text-white hover:bg-white/10">
                Retour à l'accueil
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900">
      {/* Header */}
      <div className="bg-purple-900/80 backdrop-blur-md border-b border-white/10 px-4 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/partners" className="flex items-center gap-2 text-pink-200 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span>Retour</span>
          </Link>
          <Link to="/" className="flex items-center gap-2">
            <Flower2 className="w-8 h-8 text-pink-300" />
            <span className="text-xl font-bold text-white">Fleur de Vie</span>
          </Link>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Intro */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center">
            <Building2 className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-4">
            Devenir Partenaire
          </h1>
          <p className="text-pink-200 max-w-2xl mx-auto">
            Rejoignez notre réseau de partenaires et gagnez en visibilité auprès de notre communauté 
            engagée dans l'éducation à la santé reproductive.
          </p>
        </div>

        {/* Benefits */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
          <Card className="p-4 bg-white/10 backdrop-blur-md border-white/20">
            <h3 className="text-white font-semibold mb-2">Visibilité</h3>
            <p className="text-pink-200 text-sm">
              Votre organisation sera présentée sur notre page partenaires et visible par notre communauté.
            </p>
          </Card>
          <Card className="p-4 bg-white/10 backdrop-blur-md border-white/20">
            <h3 className="text-white font-semibold mb-2">Impact Réel</h3>
            <p className="text-pink-200 text-sm">
              Contribuez à améliorer l'éducation à la santé reproductive des femmes.
            </p>
          </Card>
          <Card className="p-4 bg-white/10 backdrop-blur-md border-white/20">
            <h3 className="text-white font-semibold mb-2">Marketplace</h3>
            <p className="text-pink-200 text-sm">
              Accédez à notre audience via votre propre boutique ou catalogue de produits.
            </p>
          </Card>
        </div>

        {/* Form */}
        <Card className="p-8 bg-white/10 backdrop-blur-md border-white/20">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Organization Type */}
            <div>
              <Label className="text-white text-lg mb-4 block">Type d'Organisation *</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {organizationTypes.map((type) => (
                  <div
                    key={type.value}
                    onClick={() => updateField('organizationType', type.value)}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      formData.organizationType === type.value
                        ? 'border-pink-500 bg-pink-500/20'
                        : 'border-white/20 bg-white/5 hover:bg-white/10'
                    }`}
                    data-testid={`org-type-${type.value}`}
                  >
                    <type.icon className={`w-6 h-6 mb-2 ${
                      formData.organizationType === type.value ? 'text-pink-400' : 'text-pink-300'
                    }`} />
                    <h4 className="text-white font-medium">{type.label}</h4>
                    <p className="text-pink-200 text-xs mt-1">{type.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Organization Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label className="text-pink-200">Nom de l'Organisation *</Label>
                <Input
                  required
                  value={formData.organizationName}
                  onChange={(e) => updateField('organizationName', e.target.value)}
                  placeholder="Ex: Organisation Mondiale de la Santé"
                  className="bg-white/10 border-white/20 text-white mt-1"
                  data-testid="org-name-input"
                />
              </div>
              <div>
                <Label className="text-pink-200">Pays *</Label>
                <Input
                  required
                  value={formData.country}
                  onChange={(e) => updateField('country', e.target.value)}
                  placeholder="Ex: France"
                  className="bg-white/10 border-white/20 text-white mt-1"
                  data-testid="country-input"
                />
              </div>
            </div>

            {/* Contact Info */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <Label className="text-pink-200">Nom du Contact *</Label>
                <Input
                  required
                  value={formData.contactName}
                  onChange={(e) => updateField('contactName', e.target.value)}
                  placeholder="Prénom Nom"
                  className="bg-white/10 border-white/20 text-white mt-1"
                  data-testid="contact-name-input"
                />
              </div>
              <div>
                <Label className="text-pink-200">Email *</Label>
                <Input
                  required
                  type="email"
                  value={formData.contactEmail}
                  onChange={(e) => updateField('contactEmail', e.target.value)}
                  placeholder="contact@organisation.org"
                  className="bg-white/10 border-white/20 text-white mt-1"
                  data-testid="contact-email-input"
                />
              </div>
              <div>
                <Label className="text-pink-200">Téléphone *</Label>
                <Input
                  required
                  value={formData.contactPhone}
                  onChange={(e) => updateField('contactPhone', e.target.value)}
                  placeholder="+33 1 23 45 67 89"
                  className="bg-white/10 border-white/20 text-white mt-1"
                  data-testid="contact-phone-input"
                />
              </div>
            </div>

            {/* Website and Logo Upload */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label className="text-pink-200">Site Web</Label>
                <Input
                  value={formData.website}
                  onChange={(e) => updateField('website', e.target.value)}
                  placeholder="https://www.organisation.org"
                  className="bg-white/10 border-white/20 text-white mt-1"
                  data-testid="website-input"
                />
              </div>
              <div>
                <Label className="text-pink-200">Logo de l'Organisation</Label>
                <div className="mt-1 space-y-3">
                  <div className="flex items-center gap-4">
                    {logoPreview ? (
                      <div className="w-16 h-16 rounded-lg border-2 border-pink-400/50 overflow-hidden bg-white/10">
                        <img src={logoPreview} alt="Logo preview" className="w-full h-full object-contain" />
                      </div>
                    ) : (
                      <div className="w-16 h-16 rounded-lg border-2 border-dashed border-white/30 flex items-center justify-center bg-white/5">
                        <Image className="w-6 h-6 text-pink-300" />
                      </div>
                    )}
                    <label className="flex-1 cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                        data-testid="logo-upload-input"
                      />
                      <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 border border-white/20 hover:bg-white/20 transition-colors">
                        <Upload className="w-4 h-4 text-pink-300" />
                        <span className="text-white text-sm">
                          {uploadingLogo ? 'Téléchargement...' : logoPreview ? 'Changer le logo' : 'Télécharger un logo'}
                        </span>
                      </div>
                    </label>
                  </div>
                  <p className="text-pink-300 text-xs">Format: PNG, JPG, WEBP. Max: 5 Mo</p>
                </div>
              </div>
            </div>

            {/* Description */}
            <div>
              <Label className="text-pink-200">Description de l'Organisation *</Label>
              <Textarea
                required
                value={formData.description}
                onChange={(e) => updateField('description', e.target.value)}
                placeholder="Décrivez brièvement votre organisation, son histoire et ses activités principales..."
                rows={3}
                className="bg-white/10 border-white/20 text-white mt-1"
                data-testid="description-textarea"
              />
            </div>

            {/* Mission */}
            <div>
              <Label className="text-pink-200">Mission *</Label>
              <Textarea
                required
                value={formData.mission}
                onChange={(e) => updateField('mission', e.target.value)}
                placeholder="Quelle est la mission de votre organisation?"
                rows={2}
                className="bg-white/10 border-white/20 text-white mt-1"
                data-testid="mission-textarea"
              />
            </div>

            {/* Target Audience */}
            <div>
              <Label className="text-pink-200">Public Cible *</Label>
              <Input
                required
                value={formData.targetAudience}
                onChange={(e) => updateField('targetAudience', e.target.value)}
                placeholder="Ex: Femmes en âge de procréer, adolescentes, professionnels de santé..."
                className="bg-white/10 border-white/20 text-white mt-1"
                data-testid="target-audience-input"
              />
            </div>

            {/* Partnership Goals */}
            <div>
              <Label className="text-pink-200">Objectifs du Partenariat *</Label>
              <Textarea
                required
                value={formData.partnershipGoals}
                onChange={(e) => updateField('partnershipGoals', e.target.value)}
                placeholder="Quels sont vos objectifs en devenant partenaire de Fleur de Vie?"
                rows={3}
                className="bg-white/10 border-white/20 text-white mt-1"
                data-testid="partnership-goals-textarea"
              />
            </div>

            {/* Marketplace Section */}
            <Card className="p-4 bg-purple-500/10 border-purple-500/30">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="hasMarketplace"
                    checked={formData.hasMarketplace}
                    onChange={(e) => updateField('hasMarketplace', e.target.checked)}
                    className="w-5 h-5 rounded border-purple-400 text-purple-600 focus:ring-purple-500"
                    data-testid="marketplace-checkbox"
                  />
                  <Label htmlFor="hasMarketplace" className="text-white font-medium cursor-pointer">
                    Nous avons une marketplace / boutique en ligne
                  </Label>
                </div>
                
                {formData.hasMarketplace && (
                  <div className="space-y-4 pt-2">
                    <div>
                      <Label className="text-pink-200">URL de la Marketplace</Label>
                      <Input
                        value={formData.marketplaceUrl}
                        onChange={(e) => updateField('marketplaceUrl', e.target.value)}
                        placeholder="https://shop.votreorganisation.org"
                        className="bg-white/10 border-white/20 text-white mt-1"
                        data-testid="marketplace-url-input"
                      />
                    </div>
                    <div>
                      <Label className="text-pink-200">Description des produits/services</Label>
                      <Textarea
                        value={formData.marketplaceDescription}
                        onChange={(e) => updateField('marketplaceDescription', e.target.value)}
                        placeholder="Décrivez les produits ou services disponibles pour les utilisatrices..."
                        rows={2}
                        className="bg-white/10 border-white/20 text-white mt-1"
                        data-testid="marketplace-description-textarea"
                      />
                    </div>
                    <p className="text-purple-200 text-sm">
                      Les utilisatrices de Fleur de Vie pourront accéder à votre marketplace depuis notre application.
                    </p>
                  </div>
                )}
              </div>
            </Card>

            {/* Submit */}
            <Button
              type="submit"
              disabled={loading || !formData.organizationType}
              className="w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 h-12 text-lg"
              data-testid="submit-partner-form"
            >
              {loading ? (
                <span className="animate-pulse">Envoi en cours...</span>
              ) : (
                <>
                  <Send className="w-5 h-5 mr-2" />
                  Soumettre la Demande
                </>
              )}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default PartnerApply;
